import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./AppModal.css";

/**
 * AppModal — sistema de popups unificado da plataforma Vertex4D
 *
 * Props:
 *   isOpen           boolean       — visibilidade
 *   onClose          fn            — callback ao fechar (backdrop / ESC / X)
 *   allowOutsideClick boolean      — se fecha ao clicar fora (default true)
 *   variant          string        — 'success' | 'error' | 'warning' | 'info' | 'loading' | 'custom'
 *   title            string        — título
 *   message          string        — mensagem de apoio
 *   confirmText      string        — texto do botão primário (default 'OK')
 *   onConfirm        fn            — callback do botão primário
 *   cancelText       string        — texto do botão secundário (exibido se definido)
 *   onCancel         fn            — callback do botão secundário (fallback: onClose)
 *   children         ReactNode     — conteúdo customizado (aparece dentro do body)
 *   showCloseBtn     boolean       — exibe o X (default true, forçado false em loading)
 */
const ICONS = {
  success: <i className="fas fa-check" aria-hidden="true" />,
  error:   <i className="fas fa-times" aria-hidden="true" />,
  warning: <i className="fas fa-exclamation" aria-hidden="true" />,
  info:    <i className="fas fa-info" aria-hidden="true" />,
};

const AppModal = ({
  isOpen,
  onClose,
  allowOutsideClick = true,
  variant = "custom",
  title,
  message,
  confirmText = "OK",
  onConfirm,
  cancelText,
  onCancel,
  children,
  showCloseBtn = true,
}) => {
  const cardRef = useRef(null);

  /* ── Bloqueia scroll da página enquanto aberto ── */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  /* ── ESC fecha o modal ── */
  useEffect(() => {
    if (!isOpen || !allowOutsideClick) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, allowOutsideClick, onClose]);

  if (!isOpen) return null;

  const isLoading  = variant === "loading";
  const showClose  = showCloseBtn && !isLoading && allowOutsideClick;
  const hasIcon    = !isLoading && variant !== "custom" && ICONS[variant];
  const hasFooter  = !isLoading && (onConfirm || cancelText);

  const handleBackdrop = () => {
    if (allowOutsideClick && !isLoading) onClose?.();
  };

  return createPortal(
    <div
      className="apm-backdrop"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Modal"}
    >
      <div
        ref={cardRef}
        className="apm-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent strip */}
        <div className={`apm-accent apm-accent--${variant}`} />

        {/* Close button */}
        {showClose && (
          <button
            className="apm-close"
            onClick={onClose}
            aria-label="Fechar"
            type="button"
          >
            ×
          </button>
        )}

        {/* Body */}
        <div className="apm-body">
          {/* Spinner ou ícone */}
          {isLoading ? (
            <div className="apm-spinner" role="status" aria-label="Carregando" />
          ) : hasIcon ? (
            <div className={`apm-icon apm-icon--${variant}`}>
              {ICONS[variant]}
            </div>
          ) : null}

          {title   && <div className="apm-title">{title}</div>}
          {message && <div className="apm-message">{message}</div>}
          {children && <div className="apm-content">{children}</div>}
        </div>

        {/* Footer */}
        {hasFooter && (
          <>
            <div className="apm-divider" />
            <div className="apm-footer">
              {cancelText && (
                <button
                  type="button"
                  className="apm-btn apm-btn--secondary"
                  onClick={onCancel || onClose}
                >
                  {cancelText}
                </button>
              )}
              {onConfirm && (
                <button
                  type="button"
                  className="apm-btn apm-btn--primary"
                  onClick={onConfirm}
                >
                  {confirmText}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AppModal;
